/**
 * @fileoverview Live Stadium Interactive Stage and HUD Component for Buzzer Battle ("De barrio en barrio") (/screen).
 * Implements a Zero-Canvas GPU-accelerated architecture (60 FPS, 0% CPU in idle),
 * responsive Top HUD with Player 1 vs Player 2 scoreboard, active turn pulsing halos,
 * conditional Options Grid (dual grid vs full-width hero image depending on showOptions),
 * mobile response suspense, emerald correct pulse and point particles, crimson strike screen shake,
 * and final 3D podium with ephemeral confetti celebration.
 * 
 * @module components/Screen/Sections/BuzzerBattle/BuzzerBattleGame
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetRealtimeState, GetTheme, CalculatePodium } from '../../../../utilities/BL/BuzzerBattle';

/**
 * Option letter prefixes.
 * @constant {Array<string>}
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Confetti colors palette for victory explosion.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#00e5ff', '#ff0055', '#ffea00', '#00e676', '#ff1744', '#ffffff', '#e040fb'];

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_GAME_CONFIGURATION = {
    gameBackgroundType: 'image',
    gameBackgroundColor: '#121224',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    player1Color: '#00e5ff',
    player2Color: '#ff0055',
    fontFamily: 'Bangers',
    brandLogoUrl: '',
    sponsorLogoUrl: '',
    showOptions: true
};

/**
 * Default players template.
 * @constant {Array<Object>}
 */
const DEFAULT_PLAYERS = [
    {
        id: 'J1',
        playerIndex: 0,
        name: 'Jugador 1',
        color: '#00e5ff',
        avatar: '',
        winnerAvatarUrl: '',
        keyCode: 'KeyA',
        score: 0
    },
    {
        id: 'J2',
        playerIndex: 1,
        name: 'Jugador 2',
        color: '#ff0055',
        avatar: '',
        winnerAvatarUrl: '',
        keyCode: 'KeyL',
        score: 0
    }
];

/**
 * BuzzerBattleGame Component
 * 
 * @function BuzzerBattleGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - Incoming WebSocket packet or snapshot from parent Screen router.
 * @returns {React.ReactElement} The rendered stadium game HUD and stage.
 */
export const BuzzerBattleGame = ({ payload }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA Y CONFIGURACIÓN VISUAL
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 2. ESTADO DEL MOTOR DE JUEGO (TELEMETRÍA EN TIEMPO REAL)
    // =========================================================================
    const [gameState, setGameState] = useState({
        state: 'game_ready', // idle, cover, game_ready, question_active, turn_active, option_selected, revealed, finished
        themeId: null,
        questionSetId: null,
        themeName: '',
        questionSetTitle: '',
        currentQuestionIndex: 0,
        totalQuestions: 0,
        currentQuestion: null,
        activeTurnPlayerId: null,
        selectedOptionId: null,
        selectedOptionText: '',
        disabledOptionIds: [],
        players: DEFAULT_PLAYERS,
        showOptions: true,
        isAnswerRevealed: false,
        isCorrect: null,
        winner: null,
        finalScoresJson: null
    });

    // =========================================================================
    // 3. ESTADOS DE ANIMACIÓN GPU Y EFECTOS VISUALES
    // =========================================================================
    const [screenShakeClassName, setScreenShakeClassName] = useState('');
    const [isDamageFlashActive, setIsDamageFlashActive] = useState(false);
    const [isSuccessFlashActive, setIsSuccessFlashActive] = useState(false);
    const [scorePopupPlayer, setScorePopupPlayer] = useState(null); // 'p1' | 'p2' | null
    const [centralVerdictState, setCentralVerdictState] = useState({ visible: false, type: null, text: '', subtitle: '' });
    const [ephemeralConfettiParticleList, setEphemeralConfettiParticleList] = useState([]);

    // Timers refs para cleanup seguro (Zero Memory Leaks)
    const screenShakeTimerRef = useRef(null);
    const damageFlashTimerRef = useRef(null);
    const successFlashTimerRef = useRef(null);
    const scorePopupTimerRef = useRef(null);
    const verdictTimerRef = useRef(null);
    const confettiTimerRef = useRef(null);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    // =========================================================================
    // 4. CARGA INICIAL DE CONFIGURACIÓN Y ESTADO DE SALA
    // =========================================================================
    const fetchInitialGameConfiguration = useCallback(async () => {
        try {
            const realtimeResponse = await GetRealtimeState();
            if (realtimeResponse.success && realtimeResponse.data) {
                const roomState = realtimeResponse.data;
                if (roomState.currentTheme) {
                    setActiveThemeData(roomState.currentTheme);
                } else if (roomState.themeId) {
                    const themeResponse = await GetTheme(roomState.themeId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }

                setGameState((prevState) => ({
                    ...prevState,
                    ...roomState,
                    players: (roomState.players && roomState.players.length > 0) ? roomState.players : prevState.players
                }));
            }
        } catch (initialFetchError) {
            // Non-blocking fallback
        }
    }, []);

    useEffect(() => {
        if (!activeThemeData) {
            fetchInitialGameConfiguration();
        }
    }, [activeThemeData, fetchInitialGameConfiguration]);

    // =========================================================================
    // 5. DISPARADOR DE CONFETI EFÍMERO (3 SEGUNDOS EXACTOS - ZERO LEAKS)
    // =========================================================================
    const spawnEphemeralConfettiBurst = useCallback(() => {
        const TOTAL_CONFETTI_PARTICLES_COUNT = 70;
        const generatedParticles = Array.from({ length: TOTAL_CONFETTI_PARTICLES_COUNT }, (_, particleIndex) => {
            const randomizedColor = CONFETTI_COLOR_PALETTE[Math.floor(Math.random() * CONFETTI_COLOR_PALETTE.length)];
            const randomizedHorizontalPosition = Math.random() * 100;
            const randomizedFallDuration = 1.6 + Math.random() * 1.2;
            const randomizedDelay = Math.random() * 0.4;
            const randomizedSize = 12 + Math.random() * 16;
            const isCircular = Math.random() > 0.5;

            return {
                id: `buzzer-confetti-${particleIndex}-${Date.now()}`,
                color: randomizedColor,
                leftPercentage: randomizedHorizontalPosition,
                durationSeconds: randomizedFallDuration,
                delaySeconds: randomizedDelay,
                sizePixels: randomizedSize,
                borderRadius: isCircular ? '50%' : '2px'
            };
        });

        setEphemeralConfettiParticleList(generatedParticles);

        if (confettiTimerRef.current) {
            clearTimeout(confettiTimerRef.current);
        }

        confettiTimerRef.current = setTimeout(() => {
            setEphemeralConfettiParticleList([]);
        }, 3000);
    }, []);

    // =========================================================================
    // 6. DISPARADORES DE ANIMACIONES DE REVELACIÓN (CORRECTO / STRIKE)
    // =========================================================================
    const triggerCorrectRevealAnimation = useCallback((activePlayerId) => {
        // Emerald Flash
        setIsSuccessFlashActive(true);
        if (successFlashTimerRef.current) clearTimeout(successFlashTimerRef.current);
        successFlashTimerRef.current = setTimeout(() => {
            setIsSuccessFlashActive(false);
        }, 600);

        // Score popup
        if (activePlayerId === 'J1' || activePlayerId === 'KeyA' || activePlayerId === 'player_0') {
            setScorePopupPlayer('p1');
        } else {
            setScorePopupPlayer('p2');
        }

        if (scorePopupTimerRef.current) clearTimeout(scorePopupTimerRef.current);
        scorePopupTimerRef.current = setTimeout(() => {
            setScorePopupPlayer(null);
        }, 1200);

        // Big Central Verdict Popup (Correcto)
        setCentralVerdictState({ visible: true, type: 'correct', text: '¡CORRECTO!', subtitle: '+1 PUNTO' });
        if (verdictTimerRef.current) clearTimeout(verdictTimerRef.current);
        verdictTimerRef.current = setTimeout(() => {
            setCentralVerdictState({ visible: false, type: null, text: '', subtitle: '' });
        }, 2200);
    }, []);

    const triggerStrikeRevealAnimation = useCallback(() => {
        // Crimson Flash
        setIsDamageFlashActive(true);
        if (damageFlashTimerRef.current) clearTimeout(damageFlashTimerRef.current);
        damageFlashTimerRef.current = setTimeout(() => {
            setIsDamageFlashActive(false);
        }, 600);

        // Screen Shake
        setScreenShakeClassName('buzzer-screen-game--shake-medium');
        if (screenShakeTimerRef.current) clearTimeout(screenShakeTimerRef.current);
        screenShakeTimerRef.current = setTimeout(() => {
            setScreenShakeClassName('');
        }, 500);

        // Big Central Verdict Popup (Incorrecto)
        setCentralVerdictState({ visible: true, type: 'incorrect', text: '¡INCORRECTO!', subtitle: '' });
        if (verdictTimerRef.current) clearTimeout(verdictTimerRef.current);
        verdictTimerRef.current = setTimeout(() => {
            setCentralVerdictState({ visible: false, type: null, text: '', subtitle: '' });
        }, 2200);
    }, []);

    // =========================================================================
    // 7. PROCESAMIENTO DE MENSAJES Y TELEMETRÍA WEBSOCKET
    // =========================================================================
    useEffect(() => {
        if (!payload || !payload.type) return;

        const normalizedType = payload.type.toLowerCase();
        const incomingState = payload.state || payload.data || payload.payload || {};

        if (incomingState.currentTheme) {
            setActiveThemeData(incomingState.currentTheme);
        } else if (payload.theme) {
            setActiveThemeData(payload.theme);
        }

        switch (normalizedType) {
            case 'buzzer.prepare':
            case 'buzzer_show_game':
            case 'buzzer_match_initialized': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'game_ready',
                    isAnswerRevealed: false,
                    isCorrect: null,
                    winner: null,
                    selectedOptionId: null,
                    activeTurnPlayerId: null,
                    disabledOptionIds: []
                }));
                setEphemeralConfettiParticleList([]);
                break;
            }

            case 'buzzer_question_intro':
            case 'buzzer_question_launched': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'question_active',
                    isAnswerRevealed: false,
                    isCorrect: null,
                    selectedOptionId: null,
                    activeTurnPlayerId: null,
                    disabledOptionIds: []
                }));
                break;
            }

            case 'buzzer_options_shown': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    showOptions: true
                }));
                break;
            }

            case 'buzzer_turn_assigned': {
                const assignedPlayerId = payload.playerId || incomingState.activeTurnPlayerId || incomingState.playerId;
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    activeTurnPlayerId: assignedPlayerId,
                    state: 'turn_active'
                }));
                break;
            }

            case 'buzzer_option_selected':
            case 'buzzer_telemetry_selection': {
                const optionId = payload.data?.optionId ?? payload.optionId ?? incomingState.selectedOptionId;
                const optionText = payload.data?.optionText ?? payload.optionText ?? incomingState.selectedOptionText;
                const turnPlayerId = payload.data?.playerId ?? payload.playerId ?? incomingState.activeTurnPlayerId;

                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    selectedOptionId: optionId,
                    selectedOptionText: optionText,
                    activeTurnPlayerId: turnPlayerId || prev.activeTurnPlayerId,
                    state: 'option_selected'
                }));
                break;
            }

            case 'buzzer_option_cleared': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    state: 'turn_active'
                }));
                break;
            }

            case 'buzzer_result_revealed':
            case 'buzzer_force_updated': {
                const isCorrect = payload.isCorrect !== undefined ? payload.isCorrect : incomingState.isCorrect;
                const activePlayer = payload.activeTurnPlayerId || incomingState.activeTurnPlayerId || gameState.activeTurnPlayerId;

                if (isCorrect) {
                    triggerCorrectRevealAnimation(activePlayer);
                } else {
                    triggerStrikeRevealAnimation();
                }

                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'revealed',
                    isAnswerRevealed: true,
                    isCorrect: isCorrect
                }));
                break;
            }

            case 'buzzer_rebound':
            case 'buzzer_rebound_granted': {
                const reboundPlayerId = payload.nextPlayerId || incomingState.activeTurnPlayerId || incomingState.nextPlayerId;
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    activeTurnPlayerId: reboundPlayerId,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    isAnswerRevealed: false,
                    isCorrect: null,
                    state: 'turn_active'
                }));
                break;
            }

            case 'buzzer_final_podium':
            case 'buzzer_game_over':
            case 'buzzer_final_results': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'finished',
                    winner: payload.winner || incomingState.winner
                }));
                spawnEphemeralConfettiBurst();
                break;
            }

            case 'buzzer_match_reset':
            case 'buzzer_reset': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'idle',
                    currentQuestionIndex: 0,
                    currentQuestion: null,
                    activeTurnPlayerId: null,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    disabledOptionIds: [],
                    isAnswerRevealed: false,
                    isCorrect: null,
                    winner: null
                }));
                setEphemeralConfettiParticleList([]);
                break;
            }

            case 'buzzer_snapshot':
            case 'buzzer_state': {
                if (incomingState && typeof incomingState === 'object') {
                    setGameState((prev) => ({
                        ...prev,
                        ...incomingState
                    }));
                }
                break;
            }

            default:
                break;
        }
    }, [
        payload,
        gameState.activeTurnPlayerId,
        triggerCorrectRevealAnimation,
        triggerStrikeRevealAnimation,
        spawnEphemeralConfettiBurst
    ]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (screenShakeTimerRef.current) clearTimeout(screenShakeTimerRef.current);
            if (damageFlashTimerRef.current) clearTimeout(damageFlashTimerRef.current);
            if (successFlashTimerRef.current) clearTimeout(successFlashTimerRef.current);
            if (scorePopupTimerRef.current) clearTimeout(scorePopupTimerRef.current);
            if (verdictTimerRef.current) clearTimeout(verdictTimerRef.current);
            if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
        };
    }, []);

    // =========================================================================
    // 8. EXTRACCIÓN DE ATRIBUTOS Y CONFIGURACIÓN DEL TEMA
    // =========================================================================
    const gameBackgroundType = activeThemeData?.gameBackgroundType || DEFAULT_GAME_CONFIGURATION.gameBackgroundType;
    const gameBackgroundColor = activeThemeData?.gameBackgroundColor || DEFAULT_GAME_CONFIGURATION.gameBackgroundColor;
    const gameBackgroundUrl = activeThemeData?.gameBackgroundUrl ? resolveMediaUrl(activeThemeData.gameBackgroundUrl) : '';
    const gameTextColor = activeThemeData?.gameTextColor || DEFAULT_GAME_CONFIGURATION.gameTextColor;
    const brandLogoUrl = activeThemeData?.brandLogoUrl ? resolveMediaUrl(activeThemeData.brandLogoUrl) : '';

    const player1Config = gameState.players?.[0] || DEFAULT_PLAYERS[0];
    const player2Config = gameState.players?.[1] || DEFAULT_PLAYERS[1];

    const player1Color = player1Config.color || DEFAULT_GAME_CONFIGURATION.player1Color;
    const player2Color = player2Config.color || DEFAULT_GAME_CONFIGURATION.player2Color;

    const isTurnPlayer1 = gameState.activeTurnPlayerId === 'J1' ||
        gameState.activeTurnPlayerId === player1Config.id ||
        gameState.activeTurnPlayerId === player1Config.keyCode ||
        gameState.activeTurnPlayerId === 'player_0' ||
        gameState.activeTurnPlayerId === '0';

    const isTurnPlayer2 = gameState.activeTurnPlayerId === 'J2' ||
        gameState.activeTurnPlayerId === player2Config.id ||
        gameState.activeTurnPlayerId === player2Config.keyCode ||
        gameState.activeTurnPlayerId === 'player_1' ||
        gameState.activeTurnPlayerId === '1';

    const hasActiveTurn = isTurnPlayer1 || isTurnPlayer2;

    const currentQuestion = gameState.currentQuestion;
    const currentQuestionNumber = (gameState.currentQuestionIndex || 0) + 1;
    const totalQuestionsCount = gameState.totalQuestions || 10;
    const showOptionsActive = (activeThemeData?.showOptions ?? gameState.showOptions ?? true);

    // =========================================================================
    // 9. CÁLCULO DE PODIO FINAL
    // =========================================================================
    const podiumResult = CalculatePodium(gameState.players, gameState.finalScoresJson);
    const rankedPlayers = podiumResult.rankedPlayers || [];
    const firstPlacePlayer = rankedPlayers[0] || player1Config;
    const secondPlacePlayer = rankedPlayers[1] || player2Config;
    const isTieMatch = podiumResult.isTie;

    // =========================================================================
    // 10. RENDERIZADO DEL FONDO DINÁMICO
    // =========================================================================
    const renderGameBackgroundLayer = () => {
        if (gameBackgroundType === 'video' && gameBackgroundUrl) {
            return (
                <video
                    src={gameBackgroundUrl}
                    className="buzzer-screen-game__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (gameBackgroundType === 'image' && gameBackgroundUrl) {
            return (
                <div
                    className="buzzer-screen-game__background-image"
                    style={{ backgroundImage: `url(${gameBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="buzzer-screen-game__background-color"
                style={{ backgroundColor: gameBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 11. RENDERIZADO DE LA GRILLA DE OPCIONES Y ESTADOS DE VEREDICTO
    // =========================================================================
    const renderOptionsGrid = () => {
        if (!showOptionsActive || !currentQuestion?.options || currentQuestion.options.length === 0) {
            return null;
        }

        return (
            <div className="buzzer-screen-game__options-grid">
                {currentQuestion.options.map((optionItem, optionIndex) => {
                    const optionLetter = OPTION_LETTERS[optionIndex] || `${optionIndex + 1}`;
                    const isSelected = gameState.selectedOptionId === optionItem.id;
                    const isDisabled = gameState.disabledOptionIds?.includes(optionItem.id);
                    const isRevealed = gameState.isAnswerRevealed;
                    const isCorrect = isRevealed && (optionItem.isCorrect || (isSelected && gameState.isCorrect === true));
                    const isStrike = isRevealed && isSelected && gameState.isCorrect === false;

                    let cardModifierClass = '';
                    let badgeModifierClass = '';
                    let textModifierClass = '';

                    if (isCorrect) {
                        cardModifierClass = 'buzzer-screen-game__option-card--correct';
                        badgeModifierClass = 'buzzer-screen-game__option-letter-badge--correct';
                    } else if (isStrike) {
                        cardModifierClass = 'buzzer-screen-game__option-card--strike';
                        badgeModifierClass = 'buzzer-screen-game__option-letter-badge--strike';
                        textModifierClass = 'buzzer-screen-game__option-text--strikethrough';
                    } else if (isSelected && !isRevealed) {
                        cardModifierClass = 'buzzer-screen-game__option-card--selected';
                    } else if (isDisabled) {
                        cardModifierClass = 'buzzer-screen-game__option-card--disabled';
                        textModifierClass = 'buzzer-screen-game__option-text--strikethrough';
                    }

                    return (
                        <div
                            key={optionItem.id || optionIndex}
                            className={`buzzer-screen-game__option-card ${cardModifierClass}`}
                        >
                            <div className={`buzzer-screen-game__option-letter-badge ${badgeModifierClass}`}>
                                {optionLetter}
                            </div>

                            <span className={`buzzer-screen-game__option-text ${textModifierClass}`}>
                                {optionItem.text}
                            </span>

                            {/* Iconos de Veredicto */}
                            {isCorrect && (
                                <span className="buzzer-screen-game__verdict-icon buzzer-screen-game__verdict-icon--correct">
                                    ✓
                                </span>
                            )}
                            {isStrike && (
                                <span className="buzzer-screen-game__verdict-icon buzzer-screen-game__verdict-icon--strike">
                                    ✗
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // =========================================================================
    // 12. RENDERIZADO DEL PODIO FINAL
    // =========================================================================
    const renderPodiumStage = () => {
        const winnerAvatar = firstPlacePlayer.winnerAvatarUrl || firstPlacePlayer.avatar;
        const resolvedWinnerAvatarUrl = winnerAvatar ? resolveMediaUrl(winnerAvatar) : '';

        const secondAvatar = secondPlacePlayer.winnerAvatarUrl || secondPlacePlayer.avatar;
        const resolvedSecondAvatarUrl = secondAvatar ? resolveMediaUrl(secondAvatar) : '';

        return (
            <div className="buzzer-screen-game__podium-stage">
                <header className="buzzer-screen-game__podium-header">
                    <span className="buzzer-screen-game__podium-subtitle">
                        ¡GRAN FINAL DE BARRIO EN BARRIO!
                    </span>
                    <h1 className="buzzer-screen-game__podium-title" style={{ color: '#ffffff' }}>
                        {isTieMatch ? '¡EMPATE ÉPICO!' : `¡CAMPEÓN: ${podiumResult.winner}!`}
                    </h1>
                </header>

                <div className="buzzer-screen-game__podium-pedestals-container">
                    {/* 2° Lugar (Izquierda si no es empate) */}
                    {!isTieMatch && (
                        <div className="buzzer-screen-game__podium-pedestal buzzer-screen-game__podium-pedestal--second">
                            <div className="buzzer-screen-game__podium-avatar-wrapper">
                                {resolvedSecondAvatarUrl ? (
                                    <img
                                        src={resolvedSecondAvatarUrl}
                                        alt={secondPlacePlayer.name}
                                        className="buzzer-screen-game__podium-avatar"
                                    />
                                ) : (
                                    <div
                                        className="buzzer-screen-game__podium-avatar"
                                        style={{ backgroundColor: secondPlacePlayer.color || '#ff0055', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <span style={{ fontSize: '2.5rem', fontFamily: 'Bangers', color: '#fff' }}>
                                            {secondPlacePlayer.name?.charAt(0) || '2'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <span className="buzzer-screen-game__podium-rank-badge buzzer-screen-game__podium-rank-badge--second">
                                🥈 2° LUGAR
                            </span>
                            <span className="buzzer-screen-game__podium-player-name">
                                {secondPlacePlayer.name}
                            </span>
                            <span className="buzzer-screen-game__podium-player-score">
                                {secondPlacePlayer.score} Puntos
                            </span>
                        </div>
                    )}

                    {/* 1° Lugar / Ganador */}
                    <div
                        className={`buzzer-screen-game__podium-pedestal ${
                            isTieMatch ? 'buzzer-screen-game__podium-pedestal--tie' : 'buzzer-screen-game__podium-pedestal--first'
                        }`}
                    >
                        <span className="buzzer-screen-game__podium-crown-badge">
                            👑
                        </span>

                        <div className="buzzer-screen-game__podium-avatar-wrapper">
                            <div className="buzzer-screen-game__podium-halo" />
                            {resolvedWinnerAvatarUrl ? (
                                <img
                                    src={resolvedWinnerAvatarUrl}
                                    alt={firstPlacePlayer.name}
                                    className="buzzer-screen-game__podium-avatar"
                                />
                            ) : (
                                <div
                                    className="buzzer-screen-game__podium-avatar"
                                    style={{ backgroundColor: firstPlacePlayer.color || '#00e5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <span style={{ fontSize: '2.5rem', fontFamily: 'Bangers', color: '#fff' }}>
                                        {firstPlacePlayer.name?.charAt(0) || '1'}
                                    </span>
                                </div>
                            )}
                        </div>

                        <span className="buzzer-screen-game__podium-rank-badge buzzer-screen-game__podium-rank-badge--first">
                            {isTieMatch ? '👑 CAMPEÓN COMPARTIDO' : '🏆 1° LUGAR'}
                        </span>
                        <span className="buzzer-screen-game__podium-player-name">
                            {firstPlacePlayer.name}
                        </span>
                        <span className="buzzer-screen-game__podium-player-score">
                            {firstPlacePlayer.score} Puntos
                        </span>
                    </div>

                    {/* Segundo jugador en caso de empate */}
                    {isTieMatch && (
                        <div className="buzzer-screen-game__podium-pedestal buzzer-screen-game__podium-pedestal--tie">
                            <span className="buzzer-screen-game__podium-crown-badge">
                                👑
                            </span>

                            <div className="buzzer-screen-game__podium-avatar-wrapper">
                                <div className="buzzer-screen-game__podium-halo" />
                                {resolvedSecondAvatarUrl ? (
                                    <img
                                        src={resolvedSecondAvatarUrl}
                                        alt={secondPlacePlayer.name}
                                        className="buzzer-screen-game__podium-avatar"
                                    />
                                ) : (
                                    <div
                                        className="buzzer-screen-game__podium-avatar"
                                        style={{ backgroundColor: secondPlacePlayer.color || '#ff0055', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <span style={{ fontSize: '2.5rem', fontFamily: 'Bangers', color: '#fff' }}>
                                            {secondPlacePlayer.name?.charAt(0) || '2'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <span className="buzzer-screen-game__podium-rank-badge buzzer-screen-game__podium-rank-badge--tie">
                                👑 CAMPEÓN COMPARTIDO
                            </span>
                            <span className="buzzer-screen-game__podium-player-name">
                                {secondPlacePlayer.name}
                            </span>
                            <span className="buzzer-screen-game__podium-player-score">
                                {secondPlacePlayer.score} Puntos
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // =========================================================================
    // 13. RENDERIZADO PRINCIPAL
    // =========================================================================
    const isMatchFinished = gameState.state === 'finished';

    return (
        <div
            className={`buzzer-screen-game ${screenShakeClassName}`}
            role="region"
            aria-label="Escenario de Combate Buzzer Battle"
        >
            {/* Fondo Dinámico GPU Composited */}
            {renderGameBackgroundLayer()}

            {/* Viñeta de Contraste */}
            <div className="buzzer-screen-game__vignette-overlay" />

            {/* Flash Cinemático de Daño (Rojo) */}
            {isDamageFlashActive && (
                <div className="buzzer-screen-game__damage-flash-overlay" />
            )}

            {/* Flash Cinemático de Acierto (Verde) */}
            {isSuccessFlashActive && (
                <div className="buzzer-screen-game__success-flash-overlay" />
            )}

            {/* Partículas de Confeti Efímero */}
            {ephemeralConfettiParticleList.length > 0 && (
                <div className="buzzer-screen-game__confetti-container">
                    {ephemeralConfettiParticleList.map((confettiParticle) => (
                        <div
                            key={confettiParticle.id}
                            className="buzzer-screen-game__confetti-particle"
                            style={{
                                backgroundColor: confettiParticle.color,
                                left: `${confettiParticle.leftPercentage}%`,
                                width: `${confettiParticle.sizePixels}px`,
                                height: `${confettiParticle.sizePixels * 1.4}px`,
                                borderRadius: confettiParticle.borderRadius,
                                animationDuration: `${confettiParticle.durationSeconds}s`,
                                animationDelay: `${confettiParticle.delaySeconds}s`
                            }}
                        />
                    ))}
                </div>
            )}

            {/* Popup flotante de +1 Punto */}
            {scorePopupPlayer && (
                <div
                    className={`buzzer-screen-game__score-popup buzzer-screen-game__score-popup--${scorePopupPlayer}`}
                >
                    +1 PUNTO 🌟
                </div>
            )}

            {/* Popup Central Gigante de Veredicto (Correcto / Incorrecto) */}
            {centralVerdictState.visible && (
                <div
                    className={`buzzer-screen-game__verdict-popup buzzer-screen-game__verdict-popup--${centralVerdictState.type}`}
                >
                    <h2 className="buzzer-screen-game__verdict-title">{centralVerdictState.text}</h2>
                    {centralVerdictState.subtitle && (
                        <span className="buzzer-screen-game__verdict-subtitle">{centralVerdictState.subtitle}</span>
                    )}
                </div>
            )}

            {/* ============================================================= */}
            {/* HUD SUPERIOR: JUGADOR 1 VS JUGADOR 2 Y CONTADOR DE PREGUNTAS  */}
            {/* ============================================================= */}
            <header className="buzzer-screen-game__hud-header">
                {/* Jugador 1 Card */}
                <div
                    className={`buzzer-screen-game__hud-player buzzer-screen-game__hud-player--p1 ${
                        isTurnPlayer1 ? 'buzzer-screen-game__hud-player--active-turn' : ''
                    } ${hasActiveTurn && !isTurnPlayer1 ? 'buzzer-screen-game__hud-player--dimmed' : ''}`}
                    style={{ borderLeftColor: player1Color }}
                >
                    <div className="buzzer-screen-game__player-avatar-box" style={{ borderColor: player1Color }}>
                        {player1Config.avatar ? (
                            <img
                                src={resolveMediaUrl(player1Config.avatar)}
                                alt={player1Config.name}
                                className="buzzer-screen-game__player-avatar"
                            />
                        ) : (
                            <span className="buzzer-screen-game__player-avatar-fallback">
                                {player1Config.name?.charAt(0) || '1'}
                            </span>
                        )}
                    </div>

                    <div className="buzzer-screen-game__player-details">
                        <div className="buzzer-screen-game__player-name-row">
                            <span className="buzzer-screen-game__player-name" style={{ color: gameTextColor }}>
                                {player1Config.name}
                            </span>
                            {isTurnPlayer1 && (
                                <span className="buzzer-screen-game__turn-badge">
                                    TURNO ACTIVO
                                </span>
                            )}
                        </div>

                        <div className="buzzer-screen-game__player-score-row" style={{ color: gameTextColor }}>
                            <span className="buzzer-screen-game__player-score-value" style={{ color: player1Color }}>
                                {player1Config.score || 0}
                            </span>
                            <span className="buzzer-screen-game__player-score-label">
                                PTS
                            </span>
                        </div>
                    </div>
                </div>

                {/* Centro del HUD (Logotipo ADSL / Contador / Fase) */}
                <div className="buzzer-screen-game__hud-center">
                    <img
                        src={brandLogoUrl || '/img/page/adsl-five.png'}
                        alt="ADSL"
                        className="buzzer-screen-game__brand-logo-hud"
                    />

                    <div className="buzzer-screen-game__question-counter-badge">
                        PREGUNTA {currentQuestionNumber} DE {totalQuestionsCount}
                    </div>

                    <span className="buzzer-screen-game__status-pill">
                        {gameState.state === 'question_active' && '🔔 ¡PRESIONEN EL BOTÓN!'}
                        {gameState.state === 'turn_active' && '⚡ TURNO EN PROGRESO'}
                        {gameState.state === 'option_selected' && '⏳ RESPUESTA SELECCIONADA'}
                        {gameState.state === 'revealed' && (gameState.isCorrect ? '✅ ¡RESPUESTA CORRECTA!' : '❌ ¡RESPUESTA INCORRECTA!')}
                        {gameState.state === 'game_ready' && '🎮 PREPÁRENSE'}
                    </span>
                </div>

                {/* Jugador 2 Card */}
                <div
                    className={`buzzer-screen-game__hud-player buzzer-screen-game__hud-player--p2 ${
                        isTurnPlayer2 ? 'buzzer-screen-game__hud-player--active-turn' : ''
                    } ${hasActiveTurn && !isTurnPlayer2 ? 'buzzer-screen-game__hud-player--dimmed' : ''}`}
                    style={{ borderRightColor: player2Color }}
                >
                    <div className="buzzer-screen-game__player-avatar-box" style={{ borderColor: player2Color }}>
                        {player2Config.avatar ? (
                            <img
                                src={resolveMediaUrl(player2Config.avatar)}
                                alt={player2Config.name}
                                className="buzzer-screen-game__player-avatar"
                            />
                        ) : (
                            <span className="buzzer-screen-game__player-avatar-fallback">
                                {player2Config.name?.charAt(0) || '2'}
                            </span>
                        )}
                    </div>

                    <div className="buzzer-screen-game__player-details buzzer-screen-game__player-details--p2">
                        <div className="buzzer-screen-game__player-name-row">
                            {isTurnPlayer2 && (
                                <span className="buzzer-screen-game__turn-badge">
                                    TURNO ACTIVO
                                </span>
                            )}
                            <span className="buzzer-screen-game__player-name" style={{ color: gameTextColor }}>
                                {player2Config.name}
                            </span>
                        </div>

                        <div className="buzzer-screen-game__player-score-row" style={{ color: gameTextColor }}>
                            <span className="buzzer-screen-game__player-score-value" style={{ color: player2Color }}>
                                {player2Config.score || 0}
                            </span>
                            <span className="buzzer-screen-game__player-score-label">
                                PTS
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* ============================================================= */}
            {/* ESCENARIO CENTRAL DE PREGUNTA / IMAGEN / OPCIONES             */}
            {/* ============================================================= */}
            {!isMatchFinished && (
                <main className="buzzer-screen-game__question-stage">
                    {/* Banner de Instrucción */}
                    <div className="buzzer-screen-game__instruction-banner">
                        <h2 className="buzzer-screen-game__instruction-text" style={{ color: gameTextColor }}>
                            {currentQuestion?.instruction || '...'} {/* Cargando pregunta... */}
                        </h2>
                    </div>

                    {/* Cuerpo de la pregunta: Dual Grid si showOptions es true vs Hero Image si showOptions es false */}
                    <div
                        className={`buzzer-screen-game__question-body ${
                            showOptionsActive
                                ? 'buzzer-screen-game__question-body--with-options'
                                : 'buzzer-screen-game__question-body--hero-only'
                        }`}
                    >
                        {/* Tarjeta de Imagen Central */}
                        <div
                            className={`buzzer-screen-game__question-image-box ${
                                showOptionsActive
                                    ? 'buzzer-screen-game__question-image-box--split'
                                    : 'buzzer-screen-game__question-image-box--hero'
                            }`}
                        >
                            {currentQuestion?.imageUrl ? (
                                <img
                                    src={resolveMediaUrl(currentQuestion.imageUrl)}
                                    alt="Pregunta Visual"
                                    className="buzzer-screen-game__question-image"
                                />
                            ) : (
                                <div className="buzzer-screen-game__image-placeholder">
                                    {currentQuestion ? 'Pregunta de Texto' : 'Prepárense'}
                                </div>
                            )}
                        </div>

                        {/* Opciones de Respuesta (renderizadas condicionalmente) */}
                        {renderOptionsGrid()}
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA FINAL: PODIUM 3D                                        */}
            {/* ============================================================= */}
            {isMatchFinished && renderPodiumStage()}
        </div>
    );
};

BuzzerBattleGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object,
        winner: PropTypes.string,
        playerId: PropTypes.string,
        optionId: PropTypes.number,
        optionText: PropTypes.string,
        isCorrect: PropTypes.bool,
        nextPlayerId: PropTypes.string,
        activeTurnPlayerId: PropTypes.string
    })
};

BuzzerBattleGame.defaultProps = {
    payload: null
};
